import React, { useState, useMemo, useEffect, useRef } from 'react';
import { RecipientType } from '@/mocks/recipientsMockData';
import SearchBar from '@/components/SearchBar';
import Select from 'react-select';

import {
  usePublisherUsernames,
  usePublisherApis,
  useSubscribers,
} from '@/hooks/usePublisherQuery';
import { axiosClient } from '@/services/utilities';
import { useUsersForRecipient } from '@/hooks/useUsersForRecipient';
import UserTableForRecipient from './UserTableForRecipient';

interface AddRecipientsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (recipients: RecipientType[]) => void;
  availableRecipients: string[];
  alreadySelectedIds: string[];
  onSaveRecipients?: (usernames: string[]) => Promise<void>;
}

const AddRecipientsModal: React.FC<AddRecipientsModalProps> = ({
  isOpen,
  onClose,
  onAdd,
  availableRecipients,
  alreadySelectedIds,
  onSaveRecipients,
}) => {
  // INPUT STATE (form values, not applied to API yet)
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPublisher, setSelectedPublisher] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [selectedApi, setSelectedApi] = useState<{
    value: string;
    label: string;
  } | null>(null);

  // APPLIED FILTER STATE (what's actually sent to API)
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedPublisher, setAppliedPublisher] = useState<string | undefined>(undefined);
  const [appliedApiName, setAppliedApiName] = useState<string | undefined>(undefined);
  const [appliedApiVersion, setAppliedApiVersion] = useState<string | undefined>(undefined);

  // OTHER STATE
  const [selectedUsernames, setSelectedUsernames] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [shouldFetchSubscribers, setShouldFetchSubscribers] = useState(true);
  const [allSubscribers, setAllSubscribers] = useState<any[]>([]);
  const [isFetchingAll, setIsFetchingAll] = useState(false);
  const [subscriberCache, setSubscriberCache] = useState<Map<string, any>>(new Map()); // Cache to store subscribers from all filters

  // Cache for user data (to store complete user/subscriber info from all tables)
  const userDataCache = useRef<Map<string, { username: string; email: string; fullname: string }>>(new Map());

  // Pagination state for SUBSCRIBER table
  const [currentAfterKey, setCurrentAfterKey] = useState<string | undefined>(undefined);
  const [afterKeyHistory, setAfterKeyHistory] = useState<(string | undefined)[]>([undefined]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [maxPageReached, setMaxPageReached] = useState(1); // Track highest page reached

  // Pagination state for USER table
  const [userCurrentPage, setUserCurrentPage] = useState(1);
  const [userPageSize] = useState(10);

  // Determine which table to show: User Table (no filter) or Subscriber Table (filter active)
  const isFilterActive = Boolean(appliedPublisher);

  // Fetch publishers
  const {
    publishers,
    isLoading: isLoadingPublishers,
    refetch: refetchPublishers,
  } = usePublisherUsernames({
    size: 1000,
  });

  // Fetch APIs based on selected publisher (INPUT state)
  const { apis, isLoading: isLoadingApis } = usePublisherApis({
    publisherUsername: selectedPublisher?.value || '',
    size: 1000,
  });

  // Fetch subscribers - NOW WITH CONTROLLED ENABLED
  const {
    subscribers,
    afterKey: responseAfterKey,
    isLoading: isLoadingSubscribers,
    isError: isSubscribersError,
    error: subscribersError,
    refetch: refetchSubscribers,
  } = useSubscribers({
    publisherUsername: appliedPublisher,
    apiName: appliedApiName,
    apiVersion: appliedApiVersion,
    search: appliedSearch,
    size: 10,
    after_key: currentAfterKey,
    enabled: shouldFetchSubscribers && isFilterActive, // Only fetch subscribers when filter is active
  });

  // Fetch users for User Table (when no filter is active)
  const {
    users,
    total: usersTotal,
    isLoading: isLoadingUsers,
    isError: isUsersError,
    error: usersError,
    refetch: refetchUsers,
  } = useUsersForRecipient({
    page: userCurrentPage,
    size: userPageSize,
    search: appliedSearch,
    enabled: isOpen && !isFilterActive, // Only fetch users when no filter is active
  });

  // When modal opens: initialize state
  useEffect(() => {
    if (isOpen) {
      refetchPublishers();
      setSelectedUsernames(alreadySelectedIds || []);
      setAllSubscribers([]);
      setIsFetchingAll(false);
      setSubscriberCache(new Map()); // Clear cache when modal opens
      userDataCache.current = new Map(); // Clear user data cache

      // Reset input filter
      setSelectedPublisher(null);
      setSelectedApi(null);
      setSearchQuery('');

      // Reset applied filter
      setAppliedPublisher(undefined);
      setAppliedApiName(undefined);
      setAppliedApiVersion(undefined);
      setAppliedSearch('');

      // Reset subscriber pagination
      setCurrentAfterKey(undefined);
      setAfterKeyHistory([undefined]);
      setCurrentPage(1);
      setMaxPageReached(1);

      // Reset user pagination
      setUserCurrentPage(1);

      // IMPORTANT: Enable fetch for initial load
      setShouldFetchSubscribers(true);
    }
  }, [isOpen, alreadySelectedIds, refetchPublishers]);

  // Reset API select when publisher changes
  useEffect(() => {
    setSelectedApi(null);
  }, [selectedPublisher]);

  // Update subscriber cache when subscribers data changes
  useEffect(() => {
    if (subscribers && subscribers.length > 0) {
      setSubscriberCache((prev) => {
        const updated = new Map(prev);
        subscribers.forEach((sub) => {
          updated.set(sub.username, sub);
          // Also update userDataCache for consistent data retrieval
          userDataCache.current.set(sub.username, {
            username: sub.username,
            email: sub.email || '',
            fullname: sub.fullname || '',
          });
        });
        return updated;
      });
    }
  }, [subscribers]);

  // Update user data cache when users data changes
  useEffect(() => {
    if (users && users.length > 0) {
      users.forEach((user) => {
        userDataCache.current.set(user.username, {
          username: user.username,
          email: user.email || '',
          fullname: user.fullname || '',
        });
      });
    }
  }, [users]);

  // NEW: When user selects publisher or API (INPUT), disable fetch until Filter is clicked
  useEffect(() => {
    if (selectedPublisher || selectedApi) {
      setShouldFetchSubscribers(false);
    }
  }, [selectedPublisher, selectedApi]);

  const publisherOptions = useMemo(
    () =>
      publishers.map((pub) => ({
        value: pub.username,
        label: pub.username,
      })),
    [publishers]
  );

  const apiOptions = useMemo(
    () =>
      apis.map((api) => ({
        value: `${api.name}|||${api.version}`,
        label: `${api.name} v${api.version}`,
      })),
    [apis]
  );

  // Only show subscribers if fetch is enabled
  const displayedRecipients = useMemo(
    () => (shouldFetchSubscribers ? subscribers : []),
    [subscribers, shouldFetchSubscribers]
  );

  if (!isOpen) return null;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleToggleRecipient = (username: string) => {
    setSelectedUsernames((prev) =>
      prev.includes(username)
        ? prev.filter((u) => u !== username)
        : [...prev, username]
    );
  };

  const isRecipientSelected = (username: string) =>
    selectedUsernames.includes(username);

  // Filter button: apply filters and enable fetch (switches to Subscriber Table)
  const handleSubmitSearch = () => {
    setAppliedPublisher(selectedPublisher?.value);
    
    if (selectedApi?.value) {
      const [apiName, apiVersion] = selectedApi.value.split('|||');
      setAppliedApiName(apiName);
      setAppliedApiVersion(apiVersion);
    } else {
      setAppliedApiName(undefined);
      setAppliedApiVersion(undefined);
    }
    
    setAppliedSearch(searchQuery);
    
    // Reset subscriber pagination
    setCurrentAfterKey(undefined);
    setAfterKeyHistory([undefined]);
    setCurrentPage(1);
    setMaxPageReached(1);

    // Also reset user pagination for when user clears filter later
    setUserCurrentPage(1);
    
    // IMPORTANT: Enable fetch when Filter is clicked
    setShouldFetchSubscribers(true);
  };

  const handleClearSearch = () => {
    // Reset input
    setSelectedPublisher(null);
    setSelectedApi(null);
    setSearchQuery('');

    // Reset applied filter (this will switch back to User Table)
    setAppliedPublisher(undefined);
    setAppliedApiName(undefined);
    setAppliedApiVersion(undefined);
    setAppliedSearch('');

    // Reset subscriber pagination
    setCurrentAfterKey(undefined);
    setAfterKeyHistory([undefined]);
    setCurrentPage(1);
    setMaxPageReached(1);

    // Reset user pagination
    setUserCurrentPage(1);

    // IMPORTANT: Enable fetch
    setShouldFetchSubscribers(true);
    
    // Note: Do NOT reset selectedUsernames here - user's selections should persist
  };

  const handleNextPage = () => {
    // Only go to next page if there's an after_key AND current page has data
    if (responseAfterKey && displayedRecipients.length > 0) {
      setAfterKeyHistory((prev) => [...prev, responseAfterKey]);
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      if (nextPage > maxPageReached) {
        setMaxPageReached(nextPage);
      }
      setCurrentAfterKey(responseAfterKey);
      setTimeout(() => {
        refetchSubscribers();
      }, 0);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const newHistory = [...afterKeyHistory];
      newHistory.pop();
      const previousAfterKey = newHistory[newHistory.length - 1];

      setAfterKeyHistory(newHistory);
      setCurrentPage((prev) => prev - 1);
      setCurrentAfterKey(previousAfterKey);
      setTimeout(() => {
        refetchSubscribers();
      }, 0);
    }
  };

  // User Table pagination handlers
  const handleUserNextPage = () => {
    const totalPages = Math.ceil(usersTotal / userPageSize);
    if (userCurrentPage < totalPages) {
      setUserCurrentPage((prev) => prev + 1);
    }
  };

  const handleUserPreviousPage = () => {
    if (userCurrentPage > 1) {
      setUserCurrentPage((prev) => prev - 1);
    }
  };

  // User Table select all handler
  const handleUserToggleSelectAll = async (checked: boolean) => {
    if (!checked) {
      // Only unselect users that are currently visible on this page
      const usernamesOnPage = users.map((u) => u.username);
      setSelectedUsernames((prev) => 
        prev.filter((u) => !usernamesOnPage.includes(u))
      );
      return;
    }

    // Select all users on current page
    const usernamesOnPage = users.map((u) => u.username);
    setSelectedUsernames((prev) => [
      ...prev,
      ...usernamesOnPage.filter((u) => !prev.includes(u)),
    ]);
  };

  // FIXED: Select All with proper response structure handling
  const handleToggleSelectAll = async (checked: boolean) => {
    if (!checked) {
      setSelectedUsernames([]);
      setAllSubscribers([]);
      return;
    }

    setIsFetchingAll(true);
    try {
      const allFetched: any[] = [];
      let afterKey: string | undefined = undefined;

      const baseParams = new URLSearchParams();
      if (appliedPublisher) {
        baseParams.append('publisherUsername', appliedPublisher);
      }
      if (appliedApiName) {
        baseParams.append('apiName', appliedApiName);
      }
      if (appliedApiVersion) {
        baseParams.append('apiVersion', appliedApiVersion);
      }
      if (appliedSearch) {
        baseParams.append('search', appliedSearch);
      }
      baseParams.append('size', '100'); // Increase size for faster fetching

      console.log('Starting Select All...');

      // Loop until no more data
      let pageCount = 0;
      while (true) {
        pageCount++;
        console.log(`Fetching page ${pageCount}...`);

        const params = new URLSearchParams(baseParams);
        if (afterKey) {
          params.append('after_key', afterKey);
        }

        // Add cache buster
        params.append('_', Date.now().toString());

        const response = await axiosClient.get(
          `/subscribers?${params.toString()}`
        );

        console.log(`Page ${pageCount} response:`, response.data);

        // FIXED: Handle nested response structure
        const responseData = response.data?.data || response.data;
        const pageSubs = responseData?.subscribers || [];
        const nextAfterKey = responseData?.after_key;

        console.log(`Page ${pageCount} subscribers count:`, pageSubs.length);
        console.log(`Next after_key:`, nextAfterKey);

        // Break if no data
        if (!pageSubs || pageSubs.length === 0) {
          console.log('No more subscribers, breaking...');
          break;
        }

        allFetched.push(...pageSubs);

        // Break if no more after_key
        if (!nextAfterKey) {
          console.log('No more after_key, breaking...');
          break;
        }

        afterKey = nextAfterKey;
      }

      console.log('Total fetched:', allFetched.length);

      setAllSubscribers(allFetched);

      const allUsernames = allFetched.map((s: any) => s.username);

      setSelectedUsernames((prev) => [
        ...prev,
        ...allUsernames.filter((u) => !prev.includes(u)),
      ]);
    } catch (error) {
      console.error('Error fetching all subscribers:', error);
      // Fallback: select only current page
      const usernamesOnPage = displayedRecipients.map((r: any) => r.username);
      setSelectedUsernames((prev) => [
        ...prev,
        ...usernamesOnPage.filter((u) => !prev.includes(u)),
      ]);
    } finally {
      setIsFetchingAll(false);
    }
  };

  const isAllOnPageSelected =
    shouldFetchSubscribers &&
    isFilterActive &&
    subscribers.length > 0 &&
    subscribers.every((r: any) => isRecipientSelected(r.username));

  const handleAdd = async () => {
    const finalUsernames = selectedUsernames;

    // Use unified userDataCache to get complete data from ALL sources (users and subscribers)
    const recipientObjects: RecipientType[] = finalUsernames.map((username) => {
      // First try from unified userDataCache (includes both users and subscribers)
      const cachedData = userDataCache.current.get(username);
      if (cachedData) {
        return {
          username,
          email: cachedData.email || null,
          fullName: cachedData.fullname || null,
        };
      }
      
      // Fallback: try from subscriberCache
      let sub = subscriberCache.get(username);
      
      // If not in cache, try from allSubscribers (select all from current filter)
      if (!sub && allSubscribers.length > 0) {
        sub = allSubscribers.find((s) => s.username === username);
      }
      
      // Try from current page subscribers
      if (!sub && subscribers.length > 0) {
        sub = subscribers.find((s) => s.username === username);
      }

      // Try from current page users
      if (!sub && users.length > 0) {
        const user = users.find((u) => u.username === username);
        if (user) {
          return {
            username,
            email: user.email || null,
            fullName: user.fullname || null,
          };
        }
      }
      
      return {
        username,
        email: sub?.email || null,
        fullName: sub?.fullname || null,
      };
    });

    if (onSaveRecipients) {
      setIsSaving(true);
      try {
        await onSaveRecipients(finalUsernames);
        onAdd(recipientObjects);
        setSearchQuery('');
      } catch (error) {
        console.error('Error saving recipients:', error);
      } finally {
        setIsSaving(false);
      }
    } else {
      onAdd(recipientObjects);
      setSearchQuery('');
    }
  };

  const handleCancel = () => {
    setSearchQuery('');
    setSelectedPublisher(null);
    setSelectedApi(null);
    setSelectedUsernames(alreadySelectedIds || []);
    setAllSubscribers([]);
    setIsFetchingAll(false);
    setShouldFetchSubscribers(true);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Add Recipients</h2>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close modal"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Filter Section */}
          <div className="mb-6">
            <div className="flex items-end gap-3">
              {/* Search Input */}
              <div className="flex-1">
                <SearchBar
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search"
                  required={false}
                  label="Search by username"
                />
              </div>

              {/* Publisher Select */}
              <div className="w-48">
                <Select
                  key={`publisher-${publisherOptions.length}`}
                  options={publisherOptions}
                  placeholder="API Publisher"
                  isClearable
                  isSearchable
                  isLoading={isLoadingPublishers}
                  value={selectedPublisher}
                  onChange={(option) => setSelectedPublisher(option)}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '42px',
                      borderColor: '#d1d5db',
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                  }}
                  noOptionsMessage={() => 'No publishers found'}
                />
              </div>

              {/* API Select */}
              <div className="w-48">
                <Select
                  key={`api-${apiOptions.length}`}
                  options={apiOptions}
                  placeholder="API List"
                  isClearable
                  isSearchable
                  isLoading={isLoadingApis}
                  isDisabled={!selectedPublisher}
                  value={selectedApi}
                  onChange={(option) => setSelectedApi(option)}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '42px',
                      borderColor: '#d1d5db',
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                  }}
                  noOptionsMessage={() =>
                    selectedPublisher
                      ? 'No APIs found'
                      : 'Select a publisher first'
                  }
                />
              </div>

              {/* Filter Button */}
              <button
                onClick={handleSubmitSearch}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                Filter
              </button>

              {/* Clear Button */}
              <button
                onClick={handleClearSearch}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors whitespace-nowrap"
              >
                Clear
              </button>
            </div>
          </div>

          {/* Conditional Table Rendering: User Table (no filter) vs Subscriber Table (filter active) */}
          {!isFilterActive ? (
            /* USER TABLE - Shown when no filter is active */
            <UserTableForRecipient
              users={users}
              isLoading={isLoadingUsers}
              isError={isUsersError}
              error={usersError}
              currentPage={userCurrentPage}
              pageSize={userPageSize}
              selectedUsernames={selectedUsernames}
              onToggleRecipient={handleToggleRecipient}
              onToggleSelectAll={handleUserToggleSelectAll}
              isSelectingAll={false}
            />
          ) : (
            /* SUBSCRIBER TABLE - Shown when filter is active */
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded border-gray-300"
                            onChange={(e) =>
                              handleToggleSelectAll(e.target.checked)
                            }
                            checked={isAllOnPageSelected}
                            disabled={isFetchingAll || !shouldFetchSubscribers}
                          />
                          {isFetchingAll && (
                            <span className="text-xs text-gray-500">
                              Loading all...
                            </span>
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        No
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Username
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Full Name
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {!shouldFetchSubscribers ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-6 py-8 text-center text-gray-500"
                        >
                          Please click "Filter" to load subscribers
                        </td>
                      </tr>
                    ) : isLoadingSubscribers ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-6 py-8 text-center text-gray-500"
                        >
                          Loading subscribers...
                        </td>
                      </tr>
                    ) : isSubscribersError ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-6 py-8 text-center text-red-500"
                        >
                          <div className="flex flex-col items-center gap-2">
                            <span className="font-semibold">
                              Error loading subscribers
                            </span>
                            <span className="text-sm">
                              {subscribersError instanceof Error
                                ? subscribersError.message
                                : 'Backend error: Elasticsearch index not found. Please contact administrator.'}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ) : displayedRecipients.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-6 py-8 text-center text-gray-500"
                        >
                          No subscribers found
                        </td>
                      </tr>
                    ) : (
                      displayedRecipients.map((recipient: any, index: number) => (
                        <tr
                          key={recipient.username}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() =>
                            handleToggleRecipient(recipient.username)
                          }
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded border-gray-300"
                              checked={isRecipientSelected(recipient.username)}
                              onChange={() => {}}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {(currentPage - 1) * pageSize + index + 1}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {recipient.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {recipient.username}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {recipient.fullname}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Subscriber Table Pagination - Removed, moved to footer */}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-6 py-4 border-t border-gray-200">
          {/* Pagination Controls */}
          <div className="flex items-center">
            {!isFilterActive ? (
              /* User Table Pagination */
              <div className="flex items-center gap-3">
                <button
                  onClick={handleUserPreviousPage}
                  disabled={userCurrentPage === 1 || isLoadingUsers}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {userCurrentPage} of {Math.ceil(usersTotal / userPageSize) || 1}
                </span>
                <button
                  onClick={handleUserNextPage}
                  disabled={userCurrentPage >= Math.ceil(usersTotal / userPageSize) || isLoadingUsers}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            ) : (
              /* Subscriber Table Pagination */
              shouldFetchSubscribers && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1 || isLoadingSubscribers}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {currentPage}
                  </span>
                  <button
                    onClick={handleNextPage}
                    disabled={!responseAfterKey || isLoadingSubscribers || displayedRecipients.length === 0}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )
            )}
          </div>

          {/* Add Button */}
          <button
            onClick={handleAdd}
            disabled={selectedUsernames.length === 0 || isSaving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving...' : `Add (${selectedUsernames.length})`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddRecipientsModal;